import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Users, Video, Clock, CheckSquare, Bell, BookOpen, ArrowRight, HelpCircle, History, MessageCircle, Sparkles, Zap } from "lucide-react";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

const GoogleSpaces = () => {
  const faqData = [
    {
      question: "What happened to Google Spaces?",
      answer: "Google Spaces was discontinued on April 17, 2017, less than a year after its launch in May 2016. Google shut down the platform as part of a broader focus on other communication tools like Google Hangouts and later Google Chat."
    },
    {
      question: "Why did Google Spaces shut down?",
      answer: "Google Spaces failed to gain significant user adoption in a market already crowded with messaging and group collaboration apps. Google decided to focus resources on improving existing products rather than maintaining a struggling new platform."
    },
    {
      question: "What is the best Google Spaces alternative for study groups?",
      answer: "CramStudyWith.me is an excellent alternative for study groups and collaborative learning. It offers Study Stream for video collaboration, group study rooms, task management, and productivity tools specifically designed for educational purposes."
    },
    {
      question: "Can I recreate my Google Spaces groups elsewhere?",
      answer: "Yes! CramStudyWith.me provides modern group collaboration features including video study rooms, shared task management, and collaborative learning tools perfect for recreating study-focused communities."
    },
    {
      question: "Is CramStudyWith.me free to use?",
      answer: "Yes, CramStudyWith.me offers free access to core features including Study Stream, Study Timer, Task Management, and group study capabilities. It's a comprehensive platform for students and learners seeking collaborative study tools."
    },
    {
      question: "What features does CramStudyWith.me offer for group collaboration?",
      answer: "CramStudyWith.me offers Study Stream for live video sessions, group study rooms for collaborative learning, task management for organizing group projects, reminders for deadlines, and productivity timers to keep groups focused."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Google Spaces Alternative - Free Study Rooms | CramStudyWith.me",
    "description": "Looking for a Google Spaces alternative? Google Spaces is discontinued but CramStudyWith.me offers free Study Stream, Study Timer, and Task Management for group collaboration.",
    "url": "https://cramstudywith.me/google-spaces/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CramStudyWith.me",
      "url": "https://cramstudywith.me"
    },
    "about": {
      "@type": "Thing",
      "name": "Google Spaces Alternative"
    },
    "mentions": {
      "@type": "SoftwareApplication",
      "name": "Google Spaces",
      "applicationCategory": "CommunicationApplication"
    },
    "keywords": "Google Spaces alternative, Google Spaces replacement, Google Spaces shutdown, group messaging, study groups, collaboration platform"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CramStudyWith.me",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "ratingCount": "1000"
    },
    "featureList": [
      "Study Stream - Live video study sessions",
      "Study Timer - Pomodoro and custom timers",
      "Task Management - Organize assignments",
      "Group Study Rooms - Collaborative learning",
      "Reminders - Never miss deadlines",
      "Flash Cards - Active recall learning",
      "Quizzes - Test your knowledge",
      "Focused Collaboration - Study together"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "CramStudyWith.me"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Google Spaces Alternative - Free Study Rooms | CramStudyWith.me</title>
        <meta name="description" content="Looking for a Google Spaces alternative? Google Spaces is discontinued but CramStudyWith.me offers free Study Stream, Study Timer, and Task Management for group collaboration." />
        <meta name="keywords" content="Google Spaces alternative, Google Spaces replacement, Google Spaces shutdown, group messaging, study groups, collaboration platform, CramStudyWith.me" />
        <link rel="canonical" href="https://cramstudywith.me/google-spaces/" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full mb-6">
              <MessageCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Google Spaces Discontinued April 2017</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Google Spaces Alternative
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Google Spaces is no longer available. Discover CramStudyWith.me - the modern platform for <Link to="/study-with-me/" className="text-primary hover:underline">collaborative studying</Link> with video rooms, group collaboration, and productivity tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/studystream/">
                <Button size="lg" className="gap-2">
                  <Video className="h-5 w-5" />
                  Try Study Stream Free
                </Button>
              </Link>
              <Link to="/google-spaces/history/">
                <Button size="lg" variant="outline" className="gap-2">
                  <History className="h-5 w-5" />
                  Google Spaces History
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* What Happened Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-6 text-center">What Happened to Google Spaces?</h2>
              <div className="bg-card rounded-lg p-8 shadow-lg">
                <p className="text-lg text-muted-foreground mb-4">
                  Google Spaces was a group messaging and content sharing app that was <strong>discontinued on April 17, 2017</strong> - less than a year after its May 2016 launch. It was designed to help small groups share content and have focused conversations.
                </p>
                <p className="text-lg text-muted-foreground mb-4">
                  Despite Google's efforts, Spaces failed to differentiate itself in a crowded market dominated by Slack, Facebook Groups, and even Google's own Hangouts. The short lifespan made it one of Google's quickest product shutdowns.
                </p>
                <p className="text-lg text-muted-foreground">
                  If you're looking for group collaboration tools for studying, <Link to="/online-workspace/" className="text-primary hover:underline">CramStudyWith.me</Link> offers modern features including <Link to="/productivity-tool/" className="text-primary hover:underline">productivity tools</Link>, video collaboration, and <Link to="/study-with-friends/" className="text-primary hover:underline">group study capabilities</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Comparison */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-12 text-center">Google Spaces vs CramStudyWith.me</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Video className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Study Stream</CardTitle>
                  <CardDescription>Live video study sessions</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Go beyond text-based sharing with <Link to="/studystream/" className="text-primary hover:underline">Study Stream</Link> - real-time video collaboration for immersive group study experiences.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Users className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Group Study Rooms</CardTitle>
                  <CardDescription>Virtual study spaces</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Create focused <Link to="/group-study/" className="text-primary hover:underline">study groups</Link> with video, audio, and collaborative tools - purpose-built for learning together.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Clock className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Study Timer</CardTitle>
                  <CardDescription>Pomodoro technique</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Stay focused with our <Link to="/study-timer/" className="text-primary hover:underline">Study Timer</Link> featuring customizable Pomodoro sessions - a feature Google Spaces never had.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <CheckSquare className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Task Management</CardTitle>
                  <CardDescription>Organize group projects</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Manage assignments and projects with our <Link to="/task-management/" className="text-primary hover:underline">Task Management</Link> system - more structured than Spaces' casual sharing.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Bell className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Smart Reminders</CardTitle>
                  <CardDescription>Never miss deadlines</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Get intelligent <Link to="/reminders/" className="text-primary hover:underline">reminders</Link> for study sessions and deadlines - purpose-built productivity notifications.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <BookOpen className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Learning Tools</CardTitle>
                  <CardDescription>Flash cards & quizzes</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Access <Link to="/flash-cards/" className="text-primary hover:underline">flash cards</Link> and <Link to="/quizzes/" className="text-primary hover:underline">quizzes</Link> for active learning - educational features Google Spaces never offered.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Blog Links Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">Learn More About Group Studying</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">How Turning On My Camera Boosted My Productivity</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/blog/apps-boost-organization-college/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">Apps That Boost Organization in College</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/blog/how-to-stop-procrastination/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">How to Stop Procrastination</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-8">
                <HelpCircle className="h-8 w-8 text-primary" />
                <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Frequently Asked Questions
                </h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary to-secondary">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready for Better Group Collaboration?
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
              Join thousands of students using CramStudyWith.me for focused group study sessions and collaborative learning.
            </p>
            <Link to="/studystream/">
              <Button size="lg" variant="secondary" className="gap-2">
                Get Started Free
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <RelatedAlternatives currentPlatform="Google Spaces" />

      <Footer />
    </div>
  );
};

export default GoogleSpaces;